import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import tileSrc from "@/lib/tileSrc";

/**
 * Iter 712 — bigger, better category circles (founder: "it looks so
 * small"). Trade Me / Amazon-style photo circles that scale with the
 * viewport via CSS clamp (not breakpoints):
 *   • circle `clamp(88px, 9vw, 128px)`, column = circle + 16 px,
 *     gap `clamp(12px, 1.5vw, 24px)`
 *   • 2× Cloudinary crop for retina, lazy + async decode, explicit
 *     width/height so the row never layout-shifts
 *   • fallback chain: photo → emoji 48 px on #FFF3E8 → first letter
 *   • arrows vertically centred on the circle (not the label)
 * Exports: default carousel (home rows) + CategoryCircleGrid for the
 * /c/<root> landing pages (auto-fill minmax(112px, 1fr)).
 */

const CIRCLE = "clamp(88px, 9vw, 128px)";

function CircleTile({ item: it, href, active, testIdPrefix = "tile" }) {
  const [thumbFailed, setThumbFailed] = useState(false);
  const photo = it.thumbnail_url && !thumbFailed;
  return (
    <Link
      to={href}
      className="group flex flex-col items-center"
      data-testid={`${testIdPrefix}-${it.slug}`}
    >
      <span
        className="rounded-full bg-white overflow-hidden grid place-items-center transition-[box-shadow,transform] duration-[160ms] ease-out group-hover:-translate-y-[3px]"
        style={{
          width: CIRCLE,
          height: CIRCLE,
          border: "1px solid rgba(0,0,0,.04)",
          boxShadow: "0 4px 14px rgba(0,0,0,.08)",
          ...(active
            ? { outline: "2px solid #FF6200", outlineOffset: "4px" }
            : {}),
        }}
        data-testid={photo ? `tile-thumbnail-${it.slug}` : undefined}
        onMouseEnter={(e) => {
          e.currentTarget.style.boxShadow = "0 10px 24px rgba(255,98,0,.18)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.boxShadow = "0 4px 14px rgba(0,0,0,.08)";
        }}
      >
        {photo ? (
          <img
            src={tileSrc(it.thumbnail_url)}
            alt={it.name}
            width={128}
            height={128}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
            onError={() => setThumbFailed(true)}
          />
        ) : it.emoji ? (
          <span
            role="img"
            aria-label={it.name}
            data-testid="category-emoji"
            className="w-full h-full grid place-items-center bg-[#FFF3E8]"
            style={{ fontSize: 48, lineHeight: 1 }}
          >
            {it.emoji}
          </span>
        ) : (
          <span
            aria-hidden
            className="w-full h-full grid place-items-center bg-[#FFF3E8] text-[color:var(--brand-blue)] font-black"
            style={{ fontSize: 40, lineHeight: 1 }}
          >
            {(it.name || "?").charAt(0).toUpperCase()}
          </span>
        )}
      </span>
      <span
        className="text-center clamp-2 text-slate-700"
        style={{
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 1.25,
          marginTop: 10,
        }}
      >
        {it.name}
      </span>
    </Link>
  );
}

export default function CircularTileCarousel({
  items = [],
  hrefBuilder,
  ariaLabel = "Categories",
  testId,
  activeSlug,
}) {
  const ref = useRef(null);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setOverflows(el.scrollWidth > el.clientWidth + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    window.addEventListener("resize", check);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [items.length]);

  const scroll = (dir) => {
    if (!ref.current) return;
    ref.current.scrollBy({ left: dir * 360, behavior: "smooth" });
  };

  if (!items.length) return null;

  // Arrows sit centred on the circle (half the clamp height), not the label.
  const arrowTop = `calc(${CIRCLE} / 2)`;

  return (
    <div className="relative" data-testid={testId}>
      {overflows && (
        <button
          onClick={() => scroll(-1)}
          aria-label="Scroll left"
          className="hidden md:grid absolute -left-3 z-10 w-9 h-9 -translate-y-1/2 rounded-full bg-white text-slate-800 shadow-card place-items-center hover:bg-slate-50 transition-colors"
          style={{ top: arrowTop }}
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
      )}

      <ul
        ref={ref}
        aria-label={ariaLabel}
        className={`no-scrollbar flex items-start overflow-x-auto snap-x snap-mandatory pb-2 ${
          overflows ? "" : "justify-center"
        }`}
        style={{
          gap: "clamp(12px, 1.5vw, 24px)",
          scrollPaddingLeft: 4,
        }}
      >
        {items.map((it) => (
          <li
            key={it.id}
            className="snap-start shrink-0"
            style={{ width: `calc(${CIRCLE} + 16px)` }}
          >
            <CircleTile
              item={it}
              href={hrefBuilder(it)}
              active={activeSlug != null && activeSlug === it.slug}
            />
          </li>
        ))}
      </ul>

      {overflows && (
        <button
          onClick={() => scroll(1)}
          aria-label="Scroll right"
          className="hidden md:grid absolute -right-3 z-10 w-9 h-9 -translate-y-1/2 rounded-full bg-white text-slate-800 shadow-card place-items-center hover:bg-slate-50 transition-colors"
          style={{ top: arrowTop }}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// Iter 712 §6 — /c/<root> landing: same circles, responsive grid
// (3 columns on phones through ~8 on desktop via auto-fill).
export function CategoryCircleGrid({
  items = [],
  hrefBuilder,
  ariaLabel = "Subcategories",
  testId,
  activeSlug,
}) {
  if (!items.length) return null;
  return (
    <ul
      aria-label={ariaLabel}
      data-testid={testId}
      className="grid justify-items-center gap-y-6"
      style={{
        // 3 cols on a 390 px phone, ~5–6 on tablet, ~8 on desktop.
        gridTemplateColumns:
          "repeat(auto-fill, minmax(clamp(104px, 9vw, 132px), 1fr))",
        columnGap: "clamp(12px, 1.5vw, 24px)",
      }}
    >
      {items.map((it) => (
        <li key={it.id}>
          <CircleTile
            item={it}
            href={hrefBuilder(it)}
            active={activeSlug != null && activeSlug === it.slug}
            testIdPrefix="subcat-tile"
          />
        </li>
      ))}
    </ul>
  );
}
